import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  ApiError,
  Button,
  buttonClasses,
  CASE_STATUSES,
  cn,
  Spinner,
  type CaseStatus,
  type PartnerEnrollmentDetail,
  type PartnerEnrollmentPage,
  type PartnerEnrollmentRow,
  type PartnerEnrollmentSummary,
} from "@dearbill/shared";
import { useApi } from "../auth/useApi";
import { usePartnerMe } from "../auth/usePartnerMe";
import { useOrg } from "../shell/OrgContext";
import { ReferSomeoneModal } from "../shell/ReferSomeoneModal";
import { CaseStatusLabel } from "../components/referrals/CaseStatusLabel";
import { CASE_STATUS_CHIP_CLASS, CASE_STATUS_TONE } from "../components/referrals/caseStatus";
// The `BadgeTone` union from the component subpath, as caseStatus.ts imports it.
import type { BadgeTone } from "@dearbill/shared/components/Badge";
import calculatorIcon from "../assets/icon-calculator.svg";
import userIcon from "../assets/icon-user.svg";
import { PharmacyOnboardingInvitation } from "../components/onboarding/PharmacyOnboardingLinks";

/** How many recent referrals the status table shows before "See all". */
const TABLE_ROWS = 5;

/**
 * The Actions-needed row dot, color-coded per the row's case status via its
 * `Badge` tone, using the status pairs' text colors so the dot matches the
 * status label's hue. The color never carries meaning alone; the row text does.
 */
const TONE_DOT: Record<BadgeTone, string> = {
  pending: "bg-ochre",
  active: "bg-forest",
  error: "bg-error-red",
  neutral: "bg-deep-navy",
};

/**
 * Plain-language Current Status phrasing per row. The wording promises no
 * outcome, and a denial is named, not softened.
 */
function whereItStands(row: PartnerEnrollmentRow): ReactNode {
  // An unshared broker referral arrives as the redacted row, which carries no
  // status fields.
  if (!row.resultsShared) {
    return "Not shared with your organization.";
  }
  if (row.esignStatus === "sent") {
    return (
      <>
        Waiting for them to sign. <span className="text-ochre font-bold">Needs a nudge.</span>
      </>
    );
  }
  if (row.esignStatus !== "signed") return "Waiting for them to sign their form.";
  switch (row.status) {
    case "draft":
      return "They started their form but haven't finished it.";
    case "new":
      return "Received. DearBill is reviewing it.";
    case "need_more_information":
      return "DearBill needs more information to move this ahead.";
    case "pending_m3p_enrollment":
    case "pending_lis_enrollment":
    case "pending_pap_enrollment":
      return row.planName
        ? `Signed. DearBill filed it with ${row.planName}. Waiting for a reply.`
        : "Signed. Sent to the plan. Waiting for a reply.";
    case "denied_m3p_enrollment":
    case "denied_lis_enrollment":
    case "denied_pap_enrollment":
      return "The plan denied this request. DearBill is reviewing next steps.";
    case "member_withdrew":
      return "They withdrew their application.";
    case "enrolled_m3p":
      return "Enrolled in the Medicare Prescription Payment Plan.";
    case "enrolled_lis":
      return "Enrolled in Extra Help (LIS).";
    case "enrolled_pap":
      return "Enrolled in a pharmaceutical assistance program.";
  }
}

/** Month and day ("Sep 19") for the Referred column and the needs-action subtitle. */
function fmtMonthDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Home tab (`/orgs/:orgId/home`).
 *
 * Two action cards lead to the cost tool and to the shared
 * `ReferSomeoneModal`. When the org has referrals, an Actions needed card lists
 * those whose e-sign link was sent but not signed, the only patient-blocked
 * state the backend signals; each row carries a "Text the link again" action
 * that re-sends the sign link (`ResendLinkButton`). A Patient
 * status card shows one stat tile per case status, counted off the wire
 * `caseStatus` and linking to the Referrals page with that status filter
 * applied, plus a compact table of the newest referrals.
 *
 * The data is the same role-scoped `GET .../enrollments` list the Referrals
 * tab uses, so a `user` sees stats for their own referrals only.
 */
export function HomeTab(): ReactNode {
  const api = useApi();
  const { orgId, res } = useOrg();
  const { me } = usePartnerMe();

  // Mirrors the server's `can_take_referrals()` gate, as the Referrals tab does:
  // a Provider org mints referral links on membership alone, and other org
  // types must be active, or the referral modal's endpoints answer 403 or 409.
  // A complete but pending org can reach Home, so the button needs this gate.
  const canRefer = res.profile.organizationType === "Provider" || res.status === "active";

  const [rows, setRows] = useState<PartnerEnrollmentRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [referOpen, setReferOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setLoadError(null);
    // The overview reads only the first page of the server-paginated list
    // (`PartnerEnrollmentPage`, newest first), so the stats and the compact
    // table describe the newest referrals. The Referrals page walks the full
    // chain behind Load more.
    api<PartnerEnrollmentPage>(`/api/partner/orgs/${orgId}/enrollments`)
      .then((page) => {
        if (!cancelled) setRows(page.items);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A 401 has cleared the session in `useApi`, and the redirect takes over.
        if (err instanceof ApiError && err.status === 401) return;
        setLoadError("We couldn't load your referrals. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [api, orgId]);

  const byLabel = (row: PartnerEnrollmentRow): string =>
    me && row.partnerUserId === me.userId ? "You" : row.partnerUserName || "—";

  // An unshared broker row carries no status fields, so the tiles skip it, but
  // it counts toward "See all N referrals".
  const shared = (rows ?? []).filter(
    (r): r is PartnerEnrollmentSummary & { caseStatus: CaseStatus } =>
      r.resultsShared && r.caseStatus !== null,
  );
  const counts = new Map<CaseStatus, number>();
  for (const r of shared) counts.set(r.caseStatus, (counts.get(r.caseStatus) ?? 0) + 1);

  // The patient-blocked rows the backend can signal: e-sign link sent and not
  // signed. `none` and `expired` have no patient-side action to nudge.
  const needsAction = shared.filter((r) => r.esignStatus === "sent");

  const tableRows = (rows ?? []).slice(0, TABLE_ROWS);

  return (
    <div className="flex min-w-0 flex-col gap-6 [overflow-wrap:anywhere]">
      <PharmacyOnboardingInvitation />
      <div className="grid min-w-0 grid-cols-1 gap-5 md:grid-cols-2">
        <ActionCard
          tourAnchor="pharmacy-calculator"
          title="Member cost share calculator"
          body="Estimate how a patient's covered Part D drug costs could be spread across monthly bills."
        >
          {/* Navigates, so it is a Link, and it reuses the shared Button's
              `secondary` `lg` recipe to match a real button. */}
          <Link
            to={`/orgs/${orgId}/cost-tool`}
            className={buttonClasses(
              "secondary",
              "lg",
              "min-w-0 max-w-full gap-2 whitespace-normal px-3 [overflow-wrap:anywhere]",
            )}
          >
            <img src={calculatorIcon} alt="" className="hidden size-[18px] shrink-0 sm:block" />
            <span className="min-w-0">Start the calculator</span>
          </Link>
        </ActionCard>
        <ActionCard
          tourAnchor="pharmacy-referral"
          title="Refer a patient to DearBill"
          body="DearBill can review support options and help patients with applications."
        >
          <Button
            variant="primary"
            size="lg"
            onClick={() => setReferOpen(true)}
            disabled={!canRefer}
            title={
              canRefer
                ? "Refer a patient by QR code, link, or on this device"
                : "Referrals open up once your organization is activated"
            }
            className="min-w-0 max-w-full gap-2.5 whitespace-normal px-3 [overflow-wrap:anywhere]"
          >
            <img src={userIcon} alt="" className="hidden size-[18px] shrink-0 sm:block" />
            <span className="min-w-0">Refer a patient</span>
          </Button>
        </ActionCard>
      </div>

      <ReferSomeoneModal open={referOpen} onClose={() => setReferOpen(false)} orgId={orgId} />

      {loadError && (
        <Alert tone="error" title={loadError}>
          Reload the page to try again. If it keeps happening, use the help number at the top of the
          page.
        </Alert>
      )}

      {rows === null && !loadError && (
        <div className="text-slate-teal flex items-center gap-3">
          <Spinner /> <span>Loading your referrals…</span>
        </div>
      )}

      {rows !== null && rows.length === 0 && (
        <Card>
          <div className="px-6 py-10 text-center">
            <p className="text-foreground font-semibold">No referrals yet.</p>
            <p className="text-slate-teal mt-1">
              Patients you refer will show up here with where each one stands. To refer your first
              patient, use the &ldquo;Refer a patient&rdquo; button above.
            </p>
          </div>
        </Card>
      )}

      {rows !== null && rows.length > 0 && (
        <>
          {needsAction.length > 0 && (
            <Card>
              <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-4">
                <h2 className="text-foreground font-serif text-[21px] font-semibold leading-7">
                  Actions needed
                </h2>
              </div>
              {needsAction.map((row) => (
                <div
                  key={row.id}
                  className="border-haze flex flex-wrap items-center gap-x-[18px] gap-y-2 border-t px-6 py-4"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "size-3 shrink-0 rounded-full",
                      TONE_DOT[CASE_STATUS_TONE[row.caseStatus]],
                    )}
                  />
                  <div className="w-[220px] min-w-0">
                    <p className="text-foreground truncate text-[17px] font-semibold leading-6">
                      {row.fullName || "—"}
                    </p>
                    <p className="text-slate-teal text-sm leading-5">
                      Referred {fmtMonthDay(row.createdAt)} by{" "}
                      {byLabel(row) === "You" ? "you" : byLabel(row)}
                    </p>
                  </div>
                  <p className="text-foreground min-w-0 flex-1 text-[17px] leading-6">
                    They have not signed their form yet. DearBill texted them the link.
                  </p>
                  <ResendLinkButton orgId={orgId} enrollmentId={row.id} />
                </div>
              ))}
            </Card>
          )}

          <Card>
            <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-4">
              <h2 className="text-foreground font-serif text-[21px] font-semibold leading-7">
                Patient status
              </h2>
              <Link
                to={`/orgs/${orgId}/forms`}
                className="text-ember hover:text-ember-dark font-semibold underline underline-offset-2"
              >
                See all {rows.length} {rows.length === 1 ? "referral" : "referrals"}
              </Link>
            </div>

            {/* One tile per case status, in the Referrals page's chip order and
                rendered even at zero. Each tile links to the Referrals page with
                that status filter applied. */}
            <div className="border-haze grid grid-cols-2 gap-4 border-t p-6 sm:grid-cols-3 lg:grid-cols-5">
              {CASE_STATUSES.map((cs) => (
                <StatTile
                  key={cs}
                  n={counts.get(cs) ?? 0}
                  status={cs}
                  to={`/orgs/${orgId}/forms?status=${encodeURIComponent(cs)}`}
                />
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-deep-navy">
                  <tr className="text-[11px] font-semibold uppercase leading-[18px] tracking-[1.1px] text-white">
                    <th className="py-3 pl-6 pr-4 font-semibold">Patient</th>
                    <th className="px-4 py-3 font-semibold">Referred</th>
                    <th className="px-4 py-3 font-semibold">By</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Current Status</th>
                    <th className="py-3 pl-4 pr-6">
                      <span className="sr-only">Open</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row) => (
                    <tr key={row.id} className="border-haze border-t">
                      <td className="text-foreground whitespace-nowrap py-4 pl-6 pr-4 text-[17px] font-semibold leading-6">
                        {row.fullName || "—"}
                      </td>
                      <td className="text-foreground whitespace-nowrap px-4 py-4 text-[17px] font-medium leading-6">
                        {fmtMonthDay(row.createdAt)}
                      </td>
                      <td className="text-foreground whitespace-nowrap px-4 py-4 text-[17px] leading-6">
                        {byLabel(row)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4">
                        {row.resultsShared && row.caseStatus !== null ? (
                          <CaseStatusLabel status={row.caseStatus} />
                        ) : (
                          <span className="text-slate-teal text-sm">Not shared</span>
                        )}
                      </td>
                      <td className="text-foreground px-4 py-4 text-[17px] leading-6">
                        {whereItStands(row)}
                      </td>
                      <td className="py-4 pl-4 pr-6">
                        {/* The detail route 404s an unshared broker row, so it gets no link. */}
                        {row.resultsShared && (
                          <Link
                            to={`/orgs/${orgId}/forms/${row.id}`}
                            className="text-ember hover:text-ember-dark font-semibold underline underline-offset-2"
                          >
                            Open
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

/**
 * Display copy of `SIGN_REQUEST_NOTIFY_COOLDOWN_SECONDS` in
 * server/app/services/sign_request_notify.py. The server's 429 is the
 * authority; this only words the cooldown message.
 */
const RESEND_COOLDOWN_MINUTES = 10;

/** The chain-link icon on the resend button, drawn in `currentColor`. */
function LinkIcon(): ReactNode {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M6.67 8.67a3.33 3.33 0 0 0 5.02.36l2-2a3.33 3.33 0 0 0-4.71-4.71L7.83 3.45"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.33 7.33a3.33 3.33 0 0 0-5.02-.36l-2 2a3.33 3.33 0 0 0 4.71 4.71l1.14-1.14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The Actions-needed row's "Text the link again" action
 * (`POST .../enrollments/{id}/notify-sign-request`). The server re-sends the
 * member-portal sign link to the patient's contacts and answers 429 inside
 * its shared 10-minute cooldown, which an admin's recent send also starts, so
 * the button reports the cooldown rather than disabling pre-emptively.
 * Success and cooldown collapse to the same calm "sent recently" state; only
 * an unexpected failure reads as an error. The outcome line is aria-live so a
 * screen reader hears it without a focus move.
 */
function ResendLinkButton({
  orgId,
  enrollmentId,
}: {
  orgId: string;
  enrollmentId: string;
}): ReactNode {
  const api = useApi();
  const [state, setState] = useState<"idle" | "sending" | "sent" | "cooldown" | "error">("idle");

  const send = useCallback(() => {
    setState("sending");
    api<PartnerEnrollmentDetail>(
      `/api/partner/orgs/${orgId}/enrollments/${enrollmentId}/notify-sign-request`,
      { method: "POST" },
    )
      .then(() => setState("sent"))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 429) {
          setState("cooldown");
          return;
        }
        // A 401 has cleared the session in `useApi`, and the redirect takes over.
        if (err instanceof ApiError && err.status === 401) return;
        setState("error");
      });
  }, [api, orgId, enrollmentId]);

  if (state === "sent" || state === "cooldown") {
    return (
      <p aria-live="polite" className="text-forest shrink-0 text-[15px] font-semibold">
        {state === "sent"
          ? "Link sent."
          : `A link went out in the last ${RESEND_COOLDOWN_MINUTES} minutes.`}
      </p>
    );
  }

  return (
    <span className="flex shrink-0 flex-col items-end gap-1">
      <Button
        variant="outlined"
        size="md"
        onClick={send}
        disabled={state === "sending"}
        className="text-foreground gap-2 px-5 py-3"
      >
        <LinkIcon />
        {state === "sending" ? "Sending…" : "Text the link again"}
      </Button>
      {state === "error" && (
        <span aria-live="polite" className="text-error-red text-sm font-semibold">
          We couldn&rsquo;t send it. Try again.
        </span>
      )}
    </span>
  );
}

function Card({ children }: { children: ReactNode }): ReactNode {
  return (
    <section className="border-border-strong min-w-0 overflow-hidden rounded-2xl border bg-white">
      {children}
    </section>
  );
}

/** Primary action card: a title, body copy and one action. */
function ActionCard({
  tourAnchor,
  title,
  body,
  children,
}: {
  tourAnchor: string;
  title: string;
  body: string;
  children: ReactNode;
}): ReactNode {
  return (
    <section
      data-tour={tourAnchor}
      className="border-border-strong flex min-w-0 flex-col items-start gap-3.5 rounded-2xl border bg-white p-3 sm:p-6"
    >
      <h2 className="text-foreground font-serif text-2xl font-semibold leading-tight">{title}</h2>
      <p className="text-foreground text-[17px] leading-[26px]">{body}</p>
      <div className="mt-auto min-w-0 max-w-full pt-1">{children}</div>
    </section>
  );
}

/**
 * One stat tile, labeled with the wire `caseStatus` verbatim. The whole tile is
 * a link to the Referrals page with this status filter applied. The fill and
 * label color are the status pair's surface and text colors, the same
 * treatment as the Referrals filter chips.
 */
function StatTile({ n, status, to }: { n: number; status: CaseStatus; to: string }): ReactNode {
  return (
    <Link
      to={to}
      aria-label={`${n} referrals with status ${status} — view them`}
      className={cn(
        "block min-h-11 min-w-0 rounded-lg p-4 transition hover:ring-2 hover:ring-current",
        CASE_STATUS_CHIP_CLASS[status],
      )}
    >
      {/* 36px Outfit SemiBold per the Home design (Figma dR8cJ6hRSW5eERToo64boe
          nodes 2340:96–105), not the serif text-num-lg recipe other SPAs keep
          for financial figures. */}
      <p className="text-deep-navy font-sans text-4xl font-semibold leading-[39.6px]">{n}</p>
      <p className="mt-2 font-medium leading-[22px] text-current">{status}</p>
    </Link>
  );
}
